import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from './Layout';
import Loader from './Loader';

const ProtectedRoute = ({ children }) => {
  const { user, isAuthenticated, loading, isContractor, isContractorReadOnly, getContractorContactId, isGuardian, getGuardianId, isClient, getClientContactId, isGuardianManager, isGuardianManagerWithMaintenance, isSalesWithGuardians } = useAuth();
  const location = useLocation();

  if (loading) {
    // Render the header and a spinner, so user info is visible
    return (
      <Layout>
        <Loader />
      </Layout>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Guardian-specific routing logic - Laravel equivalent
  if (isGuardian()) {
    const guardianId = getGuardianId();
    const guardianDashboardPath = '/guardian-dashboard';

    // Allowed guardian routes - matching guardian navigation menu from Navbar.jsx
    const allowedGuardianPaths = [
      // Main dashboard
      '/guardian-dashboard',
      '/guardian-dashboard/recommend-friend-thanks',

      // Guardian queries (from navigation: "Have a query?" and "My Queries")
      '/guardian-queries',
      '/guardian-queries/create',
      '/guardian-queries/\\d+', // Query detail page

      // Property search (from navigation: "Property search")
      '/spaces/available',
      '/spaces/show/\\d+',
      '/spaces/keep-me-inform',
      '/spaces/keep-me-inform/\\d+',
      '/guardian-property-search',
      '/guardian-property-details/\\d+',

      // My Viewings (from navigation: "My Viewings")
      '/guardian-viewings',

      // Payments (from navigation: "Payments")
      '/guardian-payments',
      '/guardians/\\d+/payments', // Dynamic guardian payments route (any guardian ID)

      // Email preferences (from navigation: "Email preferences")
      '/guardian-email-preferences',

      // Edit profile (from navigation: "Edit my profile")
      '/guardian-edit-profile',
      '/guardian-edit-profile/\\d+', // Allow guardian to edit their own profile

      // Additional guardian routes
      '/guardians/empty-property-tip',
      '/keep-me-informed',
      '/guardians/license-agreement',
      '/useful-information',

      // Notice to Quit routes - guardians can create/edit notice to quit for their own rent
      '/rents/\\d+/notice-to-quit/create', // Create notice to quit
      // '/rents/\\d+/notice-to-quit/edit', // Edit notice to quit
      // '/rents/\\d+/notice-to-quit/\\d+/edit', // Edit notice to quit with notice ID
      // '/rents/\\d+/notice-to-quit/uploadFile', // Upload notice to quit file

      // Tickets routes - guardians can access tickets
      '/tickets',
      '/tickets/\\d+', // Specific ticket view
      '/tickets/create', // Create new ticket
      '/tickets/\\d+/edit', // Edit ticket
      '/tickets/\\d+/documents/create', // Create ticket document
      '/tickets/\\d+/documents/\\d+/edit', // Edit ticket document
      '/tickets/\\d+/notes/add', // Add ticket note
      '/tickets/\\d+/notes/\\d+', // View ticket note
      '/tickets/\\d+/notes/\\d+/edit', // Edit ticket note
      '/tickets/\\d+/note/create', // Create ticket note (alternative route)
      '/tickets/calendar', // Tickets calendar
      '/client-tickets', // Client tickets view
      '/clients/\\d+/tickets', // Client-specific tickets
    ];

    const isAllowedPath = allowedGuardianPaths.some(path => {
      if (typeof path === 'string' && path.includes('\\d+')) {
        // Handle regex patterns for dynamic routes
        const regexPath = new RegExp(`^${path.replace(/\\\\/g, '\\')}$`);
        return regexPath.test(location.pathname);
      }
      if (path instanceof RegExp) {
        // Handle RegExp objects directly
        return path.test(location.pathname);
      }
      return location.pathname === path;
    });

    if (!isAllowedPath) {
      // Redirect guardians to their dashboard if trying to access unauthorized routes
      return <Navigate to={guardianDashboardPath} replace />;
    }
  }

  // Contractor-specific routing logic - Laravel equivalent
  if (isContractor()) {
    const contractorId = getContractorContactId();
    const contractorCalendarPath = `/contractors/contacts/${contractorId}/calendar`;

    if (isContractorReadOnly()) {
      // CONTRACTOR-READ-ONLY: Only allow access to calendar page
      if (location.pathname !== contractorCalendarPath) {
        return <Navigate to={contractorCalendarPath} replace />;
      }
    } else {
      // CONTRACTOR: Redirect to calendar unless accessing allowed pages
      const allowedContractorPaths = [
        contractorCalendarPath,
        '/tickets', // Allow access to tickets if needed
        `/tickets/\\d+`, // Allow access to specific ticket pages
        `/tickets/\\d+/edit`, // Allow access to ticket edit pages
        `/tickets/\\d+/documents/create`, // Allow document creation
        `/tickets/\\d+/documents/\\d+/edit`, // Allow document editing
        `/tickets/\\d+/notes/add`, // Allow note creation
        `/tickets/\\d+/notes/\\d+`, // Allow note view
        `/tickets/\\d+/notes/\\d+/edit`, // Allow note editing
      ];

      const isAllowedPath = allowedContractorPaths.some(path => {
        if (path.includes('\\d+')) {
          // Handle regex patterns for dynamic routes
          const regexPath = new RegExp(`^${path.replace(/\\\\/g, '\\')}$`);
          return regexPath.test(location.pathname);
        }
        return location.pathname === path;
      });

      if (!isAllowedPath) {
        return <Navigate to={contractorCalendarPath} replace />;
      }
    }
  }

  // GUARDIAN-MANAGER-WITH-MAINTENANCE: Properties-only access
  if (isGuardianManagerWithMaintenance()) {
    const propertiesHomePath = '/properties';

    const allowedGMWithMaintenancePaths = [
      '/properties',
      '/properties/\\d+',
      '/inspections',
      '/inspections/create',
      '/inspections/\\d+/edit',
      '/properties/monthly-inspections',
      '/properties/\\d+/monthly-inspections',
      '/properties/\\d+/reports/monthly_inspections/create',
      '/properties/\\d+/reports/monthly_inspections/\\d+/edit',
      '/properties/\\d+/reports/initial_inspections/create',
      '/properties/\\d+/reports/initial_inspections/\\d+/edit',
      '/guardians/\\d+',

      // Allow /dashboard as it redirects
      '/dashboard',
    ];

    const isAllowedPath = allowedGMWithMaintenancePaths.some(path => {
      if (typeof path === 'string' && path.includes('\\d+')) {
        const regexPath = new RegExp(`^${path.replace(/\\\\/g, '\\')}$`);
        return regexPath.test(location.pathname);
      }
      if (path instanceof RegExp) {
        return path.test(location.pathname);
      }
      return location.pathname === path;
    });

    if (!isAllowedPath) {
      console.warn(`[SECURITY] GUARDIAN-MANAGER-WITH-MAINTENANCE blocked from accessing: ${location.pathname}`);
      return <Navigate to={propertiesHomePath} replace />;
    }
  }

  // Guardian Manager-specific routing logic - Laravel equivalent
  if (isGuardianManager()) {
    // const guardianManagerDashboardPath = '/guardian-manager-dashboard';
    const guardianManagerDashboardPath = '/guardians';

    // Allowed guardian manager routes - matching navigation menu from Navbar.jsx
    const allowedGuardianManagerPaths = [
      // Main dashboard
      // '/guardian-manager-dashboard',
      '/guardians',
      '/dashboard', // Also allow /dashboard as it redirects

      // Guardians management (from navigation: "Guardians" dropdown)
      '/guardians',
      '/guardians/create',
      '/guardians/\\d+', // View specific guardian
      '/guardians/\\d+/edit', // Edit specific guardian
      '/guardians/\\d+/payments', // Guardian payments
      '/guardians/after-deadline',
      '/rents/after-date-end',
      '/rents',
      '/rents/\\d+', // View specific rent
      '/rents/notice_to_quit',
      '/rooms/couples',
      '/complaints',
      '/complaints/\\d+', // View specific complaint
      '/guardian-queries',
      '/guardian-queries/create',
      '/guardian-queries/\\d+', // View specific query

      // Properties (from navigation: "Properties" dropdown)
      '/property_viewings/user/calendar',
      '/property_viewings/user/\\d+', // User-specific calendar
      '/property_viewings',
      '/property_viewings/\\d+', // View specific viewing
      '/property_viewings/\\d+/edit', // Edit specific viewing
      '/property_viewings/report',
      '/properties',
      '/properties/\\d+', // View specific property
      '/properties/vacant-rooms',
      '/properties/rooms-search',
      '/spaces/available',
      '/spaces/show/\\d+',
      '/inspections',
      '/inspections/\\d+',
      '/properties/monthly-inspections',
      '/properties/\\d+/monthly-inspections', // Property-specific monthly inspections
      '/properties/\\d+/viewings', // Property viewings button
      '/properties/\\d+/documents/\\d+/edit', // Edit property document
      '/properties/\\d+/reports/monthly_inspections/create', // Add MIR button
      '/properties/\\d+/reports/monthly_inspections/\\d+/edit', // Edit MIR
      '/properties/\\d+/reports/initial_inspections/create', // Add IR button
      '/properties/\\d+/reports/initial_inspections/\\d+/edit', // Edit IR
      '/properties/\\d+/reports/property_proposals/create', // Add PP button
      '/properties/\\d+/reports/property_proposals/\\d+/edit', // Edit PP
      '/inspection-routes',
      '/inspection-routes/\\d+',

      // Guardian Placement (from "Place Guardian" button on viewings)
      '/guardian-placement/step1/\\d+/\\d+', // Step 1: propertyId/guardianId
      '/guardian-placement/step1b/\\d+/\\d+/\\d+', // Step 1b (couple assignment): propertyId/guardianId/roomId
      '/guardian-placement/step2/\\d+/\\d+/\\d+', // Step 2: propertyId/guardianId/roomId
      '/guardian-placement/step2/\\d+/\\d+/\\d+/\\d+', // Step 2 (couple): propertyId/guardianId/roomId/secondGuardianId
      '/guardian-placement/step3/\\d+/\\d+/\\d+', // Step 3: propertyId/guardianId/roomId
      '/guardian-placement/step3/\\d+/\\d+/\\d+/\\d+', // Step 3 (couple): propertyId/guardianId/roomId/secondGuardianId

      // Tickets (from navigation: "Tickets" dropdown)
      // '/tickets',
      '/tickets/\\d+', // View specific ticket
      '/tickets/create',
      '/tickets/calendar',
      '/tickets/member/calendar',
      '/tickets/member/\\d+/calendar', // User-specific calendar
      '/tickets/\\d+/edit',
      '/tickets/\\d+/documents/create',
      '/tickets/\\d+/documents/\\d+/edit',
      '/tickets/\\d+/notes/add',
      '/tickets/\\d+/notes/\\d+',
      '/tickets/\\d+/notes/\\d+/edit',
    ];

    const isAllowedPath = allowedGuardianManagerPaths.some(path => {
      if (typeof path === 'string' && path.includes('\\d+')) {
        // Handle regex patterns for dynamic routes
        const regexPath = new RegExp(`^${path.replace(/\\\\/g, '\\')}$`);
        return regexPath.test(location.pathname);
      }
      if (path instanceof RegExp) {
        // Handle RegExp objects directly
        return path.test(location.pathname);
      }
      return location.pathname === path;
    });

    if (!isAllowedPath) {
      // Redirect guardian managers to their dashboard if trying to access unauthorized routes
      console.warn(`🚫 [SECURITY] GUARDIAN-MANAGER blocked from accessing: ${location.pathname}`);
      return <Navigate to={guardianManagerDashboardPath} replace />;
    }
  }

  // CLIENT-specific routing logic - Laravel equivalent
  if (isClient()) {
    const clientContactId = getClientContactId();
    const clientDashboardPath = '/dashboard';

    // Allowed CLIENT routes based on CLIENT permissions:
    // - clients.dashboard
    // - clients.tickets
    // - properties.show
    // - properties.notice_to_quit
    // - blocks.basic
    const allowedClientPaths = [
      // Dashboard (clients.dashboard)
      '/dashboard',

      // Client tickets (clients.tickets) - CLIENT users should use self-service routes only
      '/client-tickets',
      '/cost-of-works',
      `/tickets/\\d+`, // Ticket detail page (for CLIENT users to view their tickets)
      `/tickets/\\d+/notes/\\d+`, // View ticket note (CLIENT users can view notes)
      `/tickets/\\d+/notes/\\d+/edit`, // Edit ticket note (CLIENT users can edit their own notes - matching Laravel)
      `/tickets/\\d+/notes/add`, // Add ticket note (CLIENT users can add notes - matching Laravel)
      // Note: `/clients/:clientId/tickets` is NOT allowed for CLIENT users - redirects to dashboard

      // Properties show (properties.show)
      '/properties/\\d+', // Property detail page
      '/properties/\\d+/show', // Alternative property show route
      '/properties/\\d+/photos', // Property photos page (CLIENT users can view photos)

      // Properties notice to quit (properties.notice_to_quit)
      '/properties/\\d+/notice-to-quit', // Notice to quit list
      '/properties/\\d+/notice-to-quit/create', // Create notice to quit
      '/properties/\\d+/notice-to-quit/edit', // Edit notice to quit
      '/properties/notice_to_quit', // Notice to quit list (alternative)

      // Blocks basic (blocks.basic)
      '/blocks/\\d+', // Block detail page

      // Note: `/clients/certificates` is NOT allowed for CLIENT users - redirects to dashboard
      // CLIENT users should access certificates through property detail pages only

      // User profile edit (CLIENT users can edit their own profile)
      `/users/\\d+/edit`, // User edit page (for CLIENT users to edit their own profile)

      // Client Knowledge pages (Global Guardians services)
      '/client-knowledge/property-guardian-solution',
      '/client-knowledge/luxury-house-sitting',
      '/client-knowledge/safety-management',
      '/client-knowledge/building-management',
      '/client-knowledge/improve-your-void',
      '/client-knowledge/life-enhancing-chances',
      '/client-knowledge/property-guardian-insurance',
      '/client-knowledge/void-into-profit',
      '/client-knowledge/proptech-software-solutions',
    ];

    const isAllowedPath = allowedClientPaths.some(path => {
      if (typeof path === 'string' && path.includes('\\d+')) {
        // Handle regex patterns for dynamic routes
        const regexPath = new RegExp(`^${path.replace(/\\\\/g, '\\')}$`);
        const matches = regexPath.test(location.pathname);

        // Special security check for user edit route: CLIENT users can only access their own profile
        if (matches && path === `/users/\\d+/edit`) {
          const userIdMatch = location.pathname.match(/^\/users\/(\d+)\/edit$/);
          if (userIdMatch) {
            const urlUserId = parseInt(userIdMatch[1]);
            const loggedInUserId = user?.id;
            if (urlUserId !== loggedInUserId) {
              console.log(`[CLIENT] Access denied: Trying to access user ${urlUserId} but logged in as ${loggedInUserId}`);
              return false; // Not allowed - trying to access another user's profile
            }
          }
        }

        return matches;
      }
      if (path instanceof RegExp) {
        // Handle RegExp objects directly
        return path.test(location.pathname);
      }
      return location.pathname === path;
    });

    if (!isAllowedPath) {
      // Redirect CLIENT users to their dashboard if trying to access unauthorized routes
      console.log(`[CLIENT] Access denied to: ${location.pathname}, redirecting to dashboard`);
      return <Navigate to={clientDashboardPath} replace />;
    }
  }

  // SALES-specific routing logic - Laravel equivalent
  if (user && user.role === 'SALES') {
    const salesDashboardPath = '/clients'; // SALES role redirects to clients page

    // Allowed SALES routes based on navbar menu (salesMenu)
    const allowedSalesPaths = [
      // Main dashboard (redirects to /clients)
      '/dashboard',
      '/clients', // Main clients page

      // Add ticket button (from navigation: "Add ticket")
      '/tickets/create',

      // Payments (from navigation: "Payments")
      '/payments',
      // '/payments/\\d+', // View specific payment
      // '/payments/without-due-date',
      // '/payments/rent/\\d+/\\d+/edit', // Edit payment
      // '/payments/rent/\\d+/\\d+', // View payment
      '/rents/\\d+/payments', // Rent payments
      // '/rents/\\d+/payments/create', // Create payment
      // '/rents/\\d+/payments/\\d+/edit', // Edit payment
      // '/rents/\\d+/payments/\\d+/mark-as-paid', // Mark as paid
      '/guardians/\\d+/payments', // Guardian payments (Laravel allows this for SALES)
      '/properties/\\d+/payments', // Property payments (Laravel allows this for SALES)

      // Properties dropdown (from navigation: "Properties")
      '/property_viewings/user/calendar', // My Calendar
      '/property_viewings/user/\\d+', // User-specific calendar
      '/property_viewings', // Viewings list
      '/property_viewings/\\d+', // View specific viewing
      '/property_viewings/\\d+/edit', // Edit viewing
      '/property_viewings/create', // Create viewing
      '/property_viewings/report', // Viewing Report
      '/properties', // Properties list
      '/properties/\\d+', // View specific property
      '/properties/\\d+/show', // Alternative property show route
      '/properties/vacant-rooms', // Vacant rooms
      '/properties/\\d+/photos',
      '/properties/rooms-search', // Active rooms
      '/spaces/available', // Available spaces
      '/spaces/show/\\d+', // View specific space
      '/properties/notice-to-quit', // Notice To Quit for properties
      '/properties/\\d+/notice-to-quit', // Property-specific notice to quit
      '/inspections', // Inspections list
      '/inspections/\\d+', // View specific inspection
      '/properties/monthly-inspections', // Monthly Inspections
      '/properties/\\d+/monthly-inspections', // Property-specific monthly inspections
      '/inspection-routes', // Inspection Routes
      '/inspection-routes/\\d+', // View specific inspection route

      // Clients dropdown (from navigation: "Clients")
      // '/clients/contacts', // Contacts list
      // '/clients/contacts/\\d+', // View specific contact
      // '/clients/certificates', // Certificates list
      // '/clients/certificates/\\d+', // View specific certificate
      // '/clients/\\d+', // View specific client
      '/clients/\\d+/edit', // Edit client
      '/clients/create', // Create client

      // Tickets dropdown (from navigation: "Tickets")
      '/tickets/calendar', // Maintenance Calendar
      '/tickets/member/calendar', // My calendar
      '/tickets/member/\\d+/calendar', // Specific member calendar
      '/tickets', // Tickets list
      '/tickets/\\d+', // View specific ticket
      '/tickets/\\d+/edit', // Edit ticket
      '/tickets/\\d+/documents/create', // Create ticket document
      '/tickets/\\d+/documents/\\d+/edit', // Edit ticket document
      '/tickets/\\d+/notes/add', // Add ticket note
      '/tickets/\\d+/notes/\\d+', // View ticket note
      '/tickets/\\d+/notes/\\d+/edit', // Edit ticket note

      // Quality dropdown (from navigation: "Quality")
      '/quality/guardians/placed', // Guardians Placed
      '/guardians/registered', // Guardians Registered
      '/quality/guardians/forecasting', // Guardians Forecasting
      '/bdm/sale', // BDM for sales
    ];

    const isAllowedPath = allowedSalesPaths.some(path => {
      if (typeof path === 'string' && path.includes('\\d+')) {
        // Handle regex patterns for dynamic routes
        const regexPath = new RegExp(`^${path.replace(/\\\\/g, '\\')}$`);
        return regexPath.test(location.pathname);
      }
      if (path instanceof RegExp) {
        // Handle RegExp objects directly
        return path.test(location.pathname);
      }
      return location.pathname === path;
    });

    if (!isAllowedPath) {
      // Redirect SALES users to clients page if trying to access unauthorized routes
      console.warn(`🚫 [SECURITY] SALES blocked from accessing: ${location.pathname}`);
      return <Navigate to={salesDashboardPath} replace />;
    }
  }

  // SALES WITH GUARDIANS-specific routing logic - Laravel equivalent
  if (isSalesWithGuardians()) {
    const salesWithGuardiansDashboardPath = '/sales-with-guardians-dashboard';

    // Allowed SALES WITH GUARDIANS routes based on navbar menu and dashboard functionality
    const allowedSalesWithGuardiansPaths = [
      // Main dashboard
      '/sales-with-guardians-dashboard',
      '/dashboard', // Also allow /dashboard as it redirects

      // Tickets (from navigation: "Add ticket" button)
      '/tickets',
      '/tickets/create',
      '/tickets/\\d+', // View specific ticket
      '/tickets/\\d+/edit', // Edit ticket
      '/tickets/\\d+/documents/create', // Create ticket document
      '/tickets/\\d+/documents/\\d+/edit', // Edit ticket document
      '/tickets/\\d+/notes/add', // Add ticket note
      '/tickets/\\d+/notes/\\d+', // View ticket note
      '/tickets/\\d+/notes/\\d+/edit', // Edit ticket note
      '/tickets/\\d+/note/create', // Create ticket note (alternative route)
      '/tickets/calendar', // Tickets calendar
      '/tickets/member/calendar', // User-specific tickets calendar
      '/tickets/member/\\d+/calendar', // Specific member tickets calendar

      // Guardians dropdown (from navigation: "Guardians")
      '/rents/notice_to_quit', // Notice To Quit
      '/rents/\\d+', // View specific rent
      '/complaints', // Complaints list
      '/complaints/\\d+', // View specific complaint
      '/complaints/create', // Create complaint
      '/guardian-queries', // Guardian Queries list
      '/guardian-queries/create', // Create guardian query
      '/guardian-queries/\\d+', // View specific guardian query

      // Properties dropdown (from navigation: "Properties")
      '/property_viewings/user/calendar', // My Calendar
      '/property_viewings/user/\\d+', // User-specific calendar
      '/property_viewings', // Viewings list
      '/property_viewings/\\d+', // View specific viewing
      '/property_viewings/\\d+/edit', // Edit viewing
      '/property_viewings/\\d+/view-details', // View viewing details
      '/property_viewings/report', // Viewing Report
      '/properties/vacant-rooms', // Vacant rooms
      '/properties/rooms-search', // Active rooms
      '/spaces/available', // Available spaces
      '/spaces/show/\\d+', // View specific space
      '/properties/notice-to-quit', // Notice To Quit for properties
      '/properties/\\d+/notice-to-quit', // Property-specific notice to quit
      '/inspections', // Inspections list
      '/inspections/\\d+', // View specific inspection
      '/inspections/create', // Create inspection
      '/properties/monthly-inspections', // Monthly Inspections
      '/inspection-routes', // Inspection Routes
      '/inspection-routes/\\d+', // View specific inspection route

      // Properties (from dashboard links)
      '/properties', // Properties list
      '/properties/\\d+', // View specific property (linked from dashboard)
      '/properties/\\d+/show', // Alternative property show route

      // Guardian Placement (from "Place Guardian" button on viewings and dashboard)
      '/guardian-placement/step1/\\d+/\\d+', // Step 1: propertyId/guardianId
      '/guardian-placement/step1b/\\d+/\\d+/\\d+', // Step 1b (couple assignment): propertyId/guardianId/roomId
      '/guardian-placement/step2/\\d+/\\d+/\\d+', // Step 2: propertyId/guardianId/roomId
      '/guardian-placement/step2/\\d+/\\d+/\\d+/\\d+', // Step 2 (couple): propertyId/guardianId/roomId/secondGuardianId
      '/guardian-placement/step3/\\d+/\\d+/\\d+', // Step 3: propertyId/guardianId/roomId
      '/guardian-placement/step3/\\d+/\\d+/\\d+/\\d+', // Step 3 (couple): propertyId/guardianId/roomId/secondGuardianId

      // Guardians (from navigation: "Guardians" dropdown)
      '/guardians', // Guardians list page
      '/guardians/\\d+', // View specific guardian
      // '/guardians/\\d+/edit', // Edit specific guardian
      // '/guardians/\\d+/payments', // Guardian payments
      // '/guardians/create', // Create new guardian
      // '/guardians/after-deadline', // Guardians after deadline

      // Clients dropdown (from navigation: "Clients")
      // '/clients/contacts', // Contacts list
      // '/clients/contacts/\\d+', // View specific contact
      // '/clients/contacts/create', // Create contact
      // '/clients/contacts/\\d+/edit', // Edit contact
      '/clients/certificates', // Certificates list
      '/clients/certificates/\\d+', // View specific certificate
      // '/clients/\\d+', // View specific client
      // '/clients', // Clients list

      // Quality dropdown (from navigation: "Quality")
      '/quality/guardians/placed', // Guardians Placed
      '/guardians/registered', // Guardians Registered
      '/quality/guardians/forecasting', // Guardians Forecasting
    ];

    const isAllowedPath = allowedSalesWithGuardiansPaths.some(path => {
      if (typeof path === 'string' && path.includes('\\d+')) {
        // Handle regex patterns for dynamic routes
        const regexPath = new RegExp(`^${path.replace(/\\\\/g, '\\')}$`);
        return regexPath.test(location.pathname);
      }
      if (path instanceof RegExp) {
        // Handle RegExp objects directly
        return path.test(location.pathname);
      }
      return location.pathname === path;
    });

    if (!isAllowedPath) {
      // Redirect SALES WITH GUARDIANS users to their dashboard if trying to access unauthorized routes
      console.warn(`🚫 [SECURITY] SALES-WITH-GUARDIANS blocked from accessing: ${location.pathname}`);
      return <Navigate to={salesWithGuardiansDashboardPath} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
